// src/types.ts
import type { BookMetadata } from '../../../shared/schema';

// Re-export types used by consumers of '../types' barrel
export type { BookMetadata };
export type { PublishingChecklist, ChecklistItem, ChecklistMilestone, ChecklistStatistics, ChecklistSettings } from './types/core';
import type { PublishingChecklist } from './types/core';

// Import helper function for bookType word count defaults
import { getDefaultWordCountForBookType } from './utils/bookTypeUtils';

export interface Project {
    id: number;
    uuid: string;
    title: string;
    bookType: string;
    targetWordCount: number;
    status: string;
    progress: number;
    projectData?: any;
}

export interface BlueprintState {
  // Server existence (database flag)
  blueprintExists: boolean;           // Does blueprint exist on server?

  // Data loading state
  blueprintLoaded: boolean;           // Is blueprint data loaded in memory?
  blueprintData: any | null;          // The actual blueprint object

  // Update/save state
  blueprintUpdated: boolean;          // Has blueprint been modified (unsaved changes)?
  blueprintSaving: boolean;           // Is blueprint currently being saved?

  // Error handling
  blueprintError: string | null;      // Any blueprint-related errors

  // State management functions
  setBlueprintUpdated?: (updated: boolean) => void;  // Function to update blueprint state
  setBlueprintSaving?: (saving: boolean) => void;    // Function to update saving state
}

export interface OptimizedDataLoading {
  project: any;
  blueprint: BlueprintState;
  chapters: any[];
  isLoading: boolean;
  errors: {
    project?: string;
    blueprint?: string;
    chapters?: string;
  };
}

export interface ChapterVersion {
    content: string;
    generatedSummary: string | null;
    timestamp: number;
    revisionNotes?: string;
    previewText?: string;
}

interface FileSystemFileHandle {
    readonly kind: 'file';
    readonly name: string;
    createWritable(options?: { keepExistingData?: boolean }): Promise<FileSystemWritableFileStream>;
}

interface Window {
    showSaveFilePicker(options?: {
        suggestedName?: string;
        types?: Array<{
            description: string;
            accept: Record<string, string[]>;
        }>;
    }): Promise<FileSystemFileHandle>;
}

export interface ChapterVitals {
    povCharacter?: string;
    povType?: string;
    timelinePosition?: string;
    location?: string;
    mood?: string;
    theme?: string;
    conflict?: string;
    subplot?: string;
    wordCount?: number;
    status?: string;
    chapterStartConcept?: string;
    chapterEndConcept?: string;
    objective?: string;
    keyCharactersInChapter?: string;
    temparoryCharactersInChapter?: string;
    intenseScenes?: string;
    characterEvolutionInChapter?: string;
    keyLocationsInChapter?: string;
    culturalElementsInChapter?: string;
    authorsDirection?: string;
}

export interface Chapter {
    id: string;
    projectId: string;
    title: string;
    content: string;
    order: number;
    createdAt: string;
    updatedAt: string;
    // The following fields are likely managed in-memory by the client
    // and are not part of the core API response for a chapter list.
    // They are kept here to avoid breaking other parts of the application
    // that might rely on them, but they should be considered optional.
    summary?: string;
    plotOutline?: string;
    geography?: string;
    charactersInvolved?: string;
    characterActions?: string;
    soulOfChapter?: string;
    expectedWordCount?: number;
    status?: 'draft' | 'reviewed' | 'finalized' | 'preview_generated' | 'text_generated';
    history?: ChapterVersion[];
    historyIndex?: number;
    vitals?: ChapterVitals;
    previewContent?: string | null;
    previewSummary?: string | null;
    previewWordCountTarget?: number;
    uuid?: string;
    progress?: {
        hasPreviewText?: boolean;
        progressPercentage?: number;
        [key: string]: any;
    };
    contentCache?: {
        hasPreview?: boolean;
        previewWordCount?: number;
        hasFullText?: boolean;
        fullTextWordCount?: number;
        [key: string]: any;
    };
}

export const getActiveVersion = (chapter: Chapter | null | undefined): ChapterVersion | null => {
    if (!chapter || !Array.isArray(chapter.history) || chapter.history.length === 0 || typeof chapter.historyIndex !== 'number' || chapter.historyIndex < 0 || chapter.historyIndex >= chapter.history.length) {
        return null;
    }
    return chapter.history[chapter.historyIndex];
};

// Helper function to get project title from database project or fallback to currentProjectName
export const getProjectTitle = (dbProject?: { title: string } | null, fallbackTitle?: string | null): string => {
    return dbProject?.title || fallbackTitle || "Untitled Project";
};

// Helper function to sync blueprint.worldBuildingSuggestions.selectedStoryTitle with database project title
export const syncSelectedStoryTitleWithDB = (worldBuildingSuggestions: WorldBuildingData, dbProject?: { title: string } | null): WorldBuildingData => {
    if (dbProject?.title && dbProject.title !== worldBuildingSuggestions.selectedStoryTitle) {
        return {
            ...worldBuildingSuggestions,
            selectedStoryTitle: dbProject.title
        };
    }
    return worldBuildingSuggestions;
};

// Helper function to get target word count from database project
export const getTargetWordCount = (dbProject?: { targetWordCount: number; bookType?: string } | null, fallbackWordCount?: number): number => {
    return dbProject?.targetWordCount || fallbackWordCount || getDefaultWordCountForBookType(dbProject?.bookType || 'NOVEL');
};

// Helper function to get current word count from database project
export const getCurrentWordCount = (dbProject?: { currentWordCount?: number } | null, fallbackWordCount?: number): number => {
    return dbProject?.currentWordCount || fallbackWordCount || 0;
};

// Helper functions for theme management (consolidating themesMotifs into WorldBuildingData.themes[])
export const getThemesArray = (worldBuildingSuggestions?: WorldBuildingData): string[] => {
    return worldBuildingSuggestions?.themes || [];
};

// Helper function to convert themes array to comma-separated string for backward compatibility
export const getThemesString = (worldBuildingSuggestions?: WorldBuildingData): string => {
    return getThemesArray(worldBuildingSuggestions).join(', ');
};

// Helper function to set themes array from string or array input
export const setThemesArray = (worldBuildingSuggestions: WorldBuildingData, themes: string[] | string): WorldBuildingData => {
    const themesArray = Array.isArray(themes) ? themes : themes.split(',').map(t => t.trim()).filter(t => t.length > 0);
    return {
        ...worldBuildingSuggestions,
        themes: themesArray
    };
};

// Helper functions for genre management (consolidating genreSubject into publishingEssentials.professionalElements.metadata.genre[])
export const getGenreArray = (publishingEssentials: PublishingEssentials): string[] => {
    return publishingEssentials?.professionalElements?.metadata?.genre || [];
};

export const getGenreString = (publishingEssentials: PublishingEssentials): string => {
    const genres = getGenreArray(publishingEssentials);
    return genres.length > 0 ? genres.join(', ') : '';
};

export const setGenreArray = (publishingEssentials: PublishingEssentials, genres: string[]): PublishingEssentials => {
    return {
        ...publishingEssentials,
        professionalElements: {
            ...(publishingEssentials?.professionalElements || {
                libraryOfCongressData: { catalogingInPublication: '', subjectHeadings: [], deweyDecimal: '' },
                publisherInfo: { publisherName: '', publisherAddress: '', website: '' },
                legalNotices: { copyrightNotice: '', allRightsReserved: true, disclaimers: [], permissionsAcknowledgments: [] },
                productionCredits: { additionalCredits: [] },
                edition: { editionNumber: 1, publicationDate: '', reprintHistory: [] },
                metadata: { genre: [], keywords: [], bisacCodes: [], thematicCodes: [], targetAudience: '', contentWarnings: [], language: 'English' }
            }),
            metadata: {
                ...(publishingEssentials?.professionalElements?.metadata || { genre: [], keywords: [], bisacCodes: [], thematicCodes: [], targetAudience: '', contentWarnings: [], language: 'English' }),
                genre: genres
            }
        }
    };
};

// Advanced Cover Generation Types (Client-side)
import { CoverType, CoverElement } from '@shared/cover-elements';
import { PrintSpecs } from '@shared/print-constants';

export interface AdvancedImageRequest {
    coverType: CoverType;
    bookMetadata: {
        title: string;
        author: string;
        subtitle?: string;
        series?: string;
        blurb?: string;
        isbn?: string;
        publisher?: string;
        pageCount?: number;
        paperType?: 'white' | 'cream';
        genre?: string;
        themes?: string[];
        overallMood?: string;
        visualDescription?: string;
    };
    authorDetails?: AuthorDetails;
    selectedElements: {
        front?: CoverElement[];
        back?: CoverElement[];
    };
    printSpecs: PrintSpecs;
    generationConfig: {
        models: string[];
        artStyle: 'photorealistic' | 'illustrated' | 'minimalist' | 'genre_specific';
        layoutPreference: 'text_heavy' | 'art_focused' | 'balanced';
        backgroundOnly?: boolean;
    };
}

export interface CoverVariant {
    modelName: string;
    image: string; // base64
    metadata: {
        model: string;
        aspectRatio: string;
        dimensions: { width: number; height: number };
        printSpecs: PrintSpecs;
        estimatedCost: number;
        coverType: CoverType;
        generationTime: number;
    };
    promptUsed: string;
    elements: CoverElement[];
}

export interface MultiModelCoverResponse {
    variants: CoverVariant[];
    totalCost: number;
    coverType: CoverType;
    requestMetadata: {
        timestamp: string;
        requestId: string;
        userId: string;
        bookTitle: string;
    };
    selectionInterface: {
        previewUrl: string;
        downloadUrl: string;
        printReadyUrl: string;
    };
}

// Cover generation UI state
export interface CoverGenerationState {
    isGenerating: boolean;
    currentStep: 'setup' | 'generating' | 'selecting' | 'complete';
    selectedCoverType: CoverType;
    selectedElements: {
        front: CoverElement[];
        back: CoverElement[];
    };
    generationConfig: AdvancedImageRequest['generationConfig'];
    printSpecs: PrintSpecs;
    variants: CoverVariant[];
    selectedVariant?: CoverVariant;
    error?: string;
}

// Migrate old genreSubject to new genre array (for backward compatibility)
export const migrateGenreSubjectToArray = (genreSubject: string | undefined, publishingEssentials: PublishingEssentials): PublishingEssentials => {
    if (!genreSubject) return publishingEssentials;
    
    // Split comma-separated genre string into array
    const genres = genreSubject.split(',').map(g => g.trim()).filter(g => g.length > 0);
    const existingGenres = getGenreArray(publishingEssentials);
    
    // Merge with existing genres, avoiding duplicates
    const mergedGenres = Array.from(new Set([...existingGenres, ...genres]));
    
    return setGenreArray(publishingEssentials, mergedGenres);
};

// Helper functions for author information consolidation (eliminating redundant BackMatter author fields)
export const getAuthorBioForBackMatter = (authorDetails?: AuthorDetails): string => {
    return authorDetails?.authorBio || '';
};

export const getAuthorContactForBackMatter = (authorDetails?: AuthorDetails): string => {
    return authorDetails?.authorContact || '';
};

// Generate professional "About the Author" section from structured AuthorDetails
export const generateAboutTheAuthorSection = (authorDetails?: AuthorDetails): string => {
    if (!authorDetails) return '';
    
    let aboutSection = '';
    
    // Add author bio if available
    if (authorDetails.authorBio) {
        aboutSection += authorDetails.authorBio;
    }
    
    // Add contact/social information if available
    if (authorDetails.authorContact) {
        if (aboutSection) aboutSection += '\n\n';
        aboutSection += authorDetails.authorContact;
    }
    
    return aboutSection;
};

// Generate professional contact/social media section from structured AuthorDetails  
export const generateContactSocialMediaSection = (authorDetails?: AuthorDetails): string => {
    if (!authorDetails) return '';
    
    let contactSection = '';
    
    // Add website if available
    if (authorDetails.authorWebsite) {
        contactSection += `**Website:** ${authorDetails.authorWebsite}\n\n`;
    }
    
    // Add social media links if available
    if (authorDetails.authorSocialMedia) {
        contactSection += authorDetails.authorSocialMedia;
    }
    
    // Add general contact info if available and different from social media
    if (authorDetails.authorContact && authorDetails.authorContact !== authorDetails.authorSocialMedia) {
        if (contactSection) contactSection += '\n\n';
        contactSection += '**Contact:** ' + authorDetails.authorContact;
    }
    
    return contactSection;
};

// Consolidate BackMatter author fields to use structured data (removes redundancy)
export const consolidateBackMatterAuthorInfo = (backMatter: BackMatter, authorDetails?: AuthorDetails): BackMatter => {
    return {
        ...backMatter,
        aboutTheAuthor: generateAboutTheAuthorSection(authorDetails),
        contactSocialMedia: generateContactSocialMediaSection(authorDetails)
    };
};

// Import BookMetadata from shared schema (single source of truth)


// --- World Building Types ---
// Character interface - synced with shared schema
export interface Character {
    id: string;
    name: string;
    description: string;
    role?: string;
    personality?: string;
    background?: string;
    goals?: string;
    conflicts?: string;
}

// Legacy alias for backward compatibility
export interface CharacterProfile {
    id: string;
    name: string;
    briefDescription: string;
}

// Extended character interface for detailed editing
export interface DetailedCharacter extends Character {
    backstory?: string;
    appearance?: string;
    relationships?: string;
    developmentArc?: string;
    notes?: string;
}

// Location interface - synced with shared schema
export interface Location {
    id: string;
    name: string;
    description: string;
    significance?: string;
    atmosphere?: string;
    details?: string;
}

// Legacy alias for backward compatibility
export interface LocationProfile {
    id: string;
    name: string;
    briefDescription: string;
}

// Custom category item interface - synced with shared schema
export interface CustomCategoryItem {
    id: string;
    name: string;
    description: string;
}

// Custom category interface - synced with shared schema
export interface CustomCategory {
    id: string;
    name: string;
    description: string;
    entries: CustomCategoryItem[];
}

// Legacy aliases for backward compatibility
export interface CustomWorldBuildingCategoryItem {
    id: string;
    name: string;
    description: string;
}
export interface CustomWorldBuildingCategory {
    id: string;
    categoryName: string;
    items: CustomWorldBuildingCategoryItem[];
}

// Blueprint interface - Creative elements ONLY (synced with shared schema)
export interface WorldBuildingSuggestions {
    storyTitleIdeas: string[];
    logLine: string;
    storyStartConcept: string;
    storyEndConcept: string;
    characters: Character[];
    locations: Location[];
    customCategories: CustomCategory[];
}

// Legacy interface for backward compatibility
export interface WorldBuildingData {
    storyTitleIdeas: string[];
    selectedStoryTitle: string;
    logLine: string;
    storyStartConcept: string;
    storyEndConcept: string;
    characters: DetailedCharacter[];
    locations: LocationProfile[];
    customCategories: CustomWorldBuildingCategory[];
    themes: string[]; // Added missing property
}
// Blueprint response types - matching server types
export interface BlueprintResponse {
    worldBuildingSuggestions: WorldBuildingSuggestions;
}

export interface MetadataGenerationResponse {
    bookMetadata: BookMetadata;
}

export interface InitialBlueprintResponse {
    blueprint: BlueprintResponse;
    bookMetadata: BookMetadata;
    hasInitialBlueprint: boolean;
}

// --- End World Building Types ---

// --- Author Details ---
export interface AuthorDetails {
    authorName: string;
    authorBio: string; // Markdown
    authorWebsite: string;
    authorSocialMedia: string; // Markdown (for multiple links)
    authorContact: string; // Markdown
    authorProfileImageBase64: string | null;
    // Copyright & Publishing Information
    copyrightYear: string;
    isbn: string;
    publisher: string;
    edition: string;
    copyrightNotice: string; // Markdown

    // Professional Marketing Elements for Cover Generation
    publisherLogoBase64?: string | null;        // Publisher logo image
    frontCoverEndorsements?: string;            // Markdown - endorsements for front cover
    awardsAndBadges?: string[];                 // Array of awards/achievements

    // Back Cover Marketing Elements
    backCoverHookLine?: string;                 // Catchy one-liner for back cover
    backCoverEndorsements?: string;             // Markdown - review quotes
    seriesDisplayInfo?: string;                 // How to display series info on covers
    qrCodeUrl?: string;                         // URL for QR code generation
    socialMediaHandles?: string;                // Social handles for back cover
}
// --- End Author Details ---


// --- Book Structure Types ---
export interface FrontMatter {
    content: string; // Consolidated markdown
    titlePageContent: string; // Markdown
    titlePageImageBase64: string | null; 
    // Title Image Generation fields
    titleImagePrompt: string;
    titleImageAspectRatio: string; // 16:9, 4:3, 1:1, 3:4, 9:16
    titleImageBase64: string | null; 
    copyrightPageContent: string; // Markdown
    dedication: string; // Markdown
    epigraph: string; // Markdown
    tableOfContents: string; // Markdown (manual for now, AI can generate draft)
    foreword: string; // Markdown
    forewordBy: string;
    preface: string; // Markdown
    acknowledgmentsFront: string; // Markdown
    prologue: string; // Markdown (for fiction)
    introduction: string; // Markdown (for non-fiction)
}

export interface AppendixItem {
    id: string;
    title: string;
    content: string; // Markdown
}

export interface GlossaryTerm {
    id: string;
    term: string;
    definition: string;
}

export interface BibliographyEntry {
    id: string;
    citation: string; // Markdown
}

export interface BackMatter {
    content: string; // Consolidated markdown
    epilogue: string; // Markdown
    afterword: string; // Markdown
    authorsNote: string; // Markdown
    acknowledgmentsBack: string; // Markdown
    appendices: AppendixItem[];
    glossary: GlossaryTerm[];
    bibliography: BibliographyEntry[];
    index: string; // Markdown (manual, AI can generate draft)
    readingGroupGuide: string; // Markdown
    teaserNextBook: string; // Markdown
    aboutTheAuthor: string; // Markdown
    contactSocialMedia: string; // Markdown
    backCoverImageBase64: string | null;
    // Back Cover Image Generation fields
    backCoverImagePrompt: string;
    backCoverImageAspectRatio: string; // 16:9, 4:3, 1:1, 3:4, 9:16
    backCoverImageGeneratedBase64: string | null;
}

export interface PublishingEssentials {
    metadataFileNotes: string; // Markdown
    queryLetterDraft: string; // Markdown
    marketingBlurb: string; // Markdown
    coverDesignBrief: string; // Markdown
    isbnNotes: string; // Markdown
    copyrightRegistrationNotes: string; // Markdown
    mediumFormat?: 'PDF' | 'EPUB' | 'both';
    professionalElements?: ProfessionalElements; // Professional publishing metadata
}

// Professional Publishing Elements interfaces (imported from utils/publishingElements.ts)
export interface ProfessionalElements {
  libraryOfCongressData: LibraryOfCongressData;
  publisherInfo: PublisherInfo;
  legalNotices: LegalNotices;
  productionCredits: ProductionCredits;
  edition: Edition;
  metadata: PublishingMetadata;
}

export interface LibraryOfCongressData {
  catalogingInPublication: string;
  subjectHeadings: string[];
  deweyDecimal: string;
  lccn?: string;
  isbn13?: string;
  isbn10?: string;
}

export interface PublisherInfo {
  publisherName: string;
  imprintName?: string;
  publisherAddress: string;
  website: string;
  email?: string;
  phone?: string;
}

export interface LegalNotices {
  copyrightNotice: string;
  allRightsReserved: boolean;
  trademarkNotice?: string;
  disclaimers: string[];
  permissionsAcknowledgments: string[];
}

export interface ProductionCredits {
  editor?: string;
  developmentalEditor?: string;
  copyEditor?: string;
  proofreader?: string;
  coverDesigner?: string;
  interiorDesigner?: string;
  illustrator?: string;
  photographer?: string;
  indexer?: string;
  translator?: string;
  narrator?: string;
  additionalCredits: Array<{
    role: string;
    name: string;
    website?: string;
  }>;
}

export interface Edition {
  editionNumber: number;
  editionName?: string;
  printRun?: number;
  publicationDate: string;
  reprintHistory: Array<{
    date: string;
    changes: string;
    printRun?: number;
  }>;
}

export interface PublishingMetadata {
  genre: string[];
  keywords: string[];
  bisacCodes: string[];
  thematicCodes: string[];
  targetAudience: string;
  contentWarnings: string[];
  language: string;
  pageCount?: number;
  wordCount?: number;
}
// --- End Book Structure Types ---

export type MainView = 'ideaHub' | 'bookDetails' | 'worldBuilding' | 'characters' | 'bookStructureStudio' | 'chaptersBuilding' | 'publishingEssentials' | 'bookPreview' | 'aiTools';


export interface ProjectState {
    projectId?: string;
    currentProjectId?: number;
    outlineIdea: string;
    targetPageCount: string; 
    targetWordCount?: number; // Add targetWordCount field for edit mode
    chapters: Chapter[];
    bookMetadata: BookMetadata; 
    // SINGLE SOURCE OF TRUTH: Use blueprint.worldBuildingSuggestions instead of worldBuildingData
    blueprint: {
        bookMetadata: BookMetadata;
        worldBuildingSuggestions: WorldBuildingData;
    };
    authorDetails: AuthorDetails; 
    activeChapterId: string | null;
    currentProjectName: string | null;
    isUnsaved: boolean;
    hasInitialBlueprint: boolean;
    selectedChapterIdsForMerge: string[];
    chapterPreviewPercentage: number; 
    exportChapterContentPreference: 'preview' | 'content';

    bookMetadataSnapshotForChapters: BookMetadata | null;
    // REMOVED: worldBuildingData snapshot fields - use blueprint snapshots instead
    blueprintSnapshotForChapters: { bookMetadata: BookMetadata; worldBuildingSuggestions: WorldBuildingData; } | null;
    blueprintSnapshotForHolisticRegen: { bookMetadata: BookMetadata; worldBuildingSuggestions: WorldBuildingData; } | null;

    frontMatter: FrontMatter;
    backMatter: BackMatter;
    publishingEssentials: PublishingEssentials;
    publishingChecklist?: PublishingChecklist;
    exportHistory?: ExportRecord[];
}

export interface StoredProject extends ProjectState { 
    projectName: string;
    lastModified: number;
}

export interface ChapterDetailEdits extends Omit<Chapter, 'id' | 'history' | 'historyIndex' | 'status' | 'expectedWordCount' | 'vitals' | 'previewContent' | 'previewSummary' | 'previewWordCountTarget'> {
    expectedWordCount: string; 
    vitals: ChapterVitals; 
    previewText?: string;
    fullText?: string;
}


export interface LoadingStates {
    [key: string]: boolean | undefined;
    initialBlueprintGen?: boolean;
    chaptersGen?: boolean; 
    genText?: boolean; 
    genPreview?: boolean; 
    genAllShortPreviews?: boolean; 
    genAllPreviewsToFull?: boolean; 
    reviseText?: boolean;
    cover?: boolean;
    save?: boolean; 
    splitChapter?: boolean; 
    mergeChapter?: boolean;
    parseCharacters?: boolean; 
    parseLocations?: boolean;  
    generateCustomCategoryItems?: boolean; 
    refineCustomCategoryItems?: boolean;   
    regenerateAllWorldBuilding?: boolean; 
    importProject?: boolean; 
    generateFrontMatterElement?: boolean;
    generateBackMatterElement?: boolean;
    generateMultipleStructureElements?: boolean;
    generateTitlePageImage?: boolean;
    generateBackCoverImage?: boolean;
    generateTableOfContents?: boolean; // New
    generateIndex?: boolean; // New
}

// Type for updating BackMatter list items with discriminated union for type safety
export type UpdateBackMatterListItemArgsTuple =
    | [listField: 'appendices', id: string, itemField: keyof Omit<AppendixItem, 'id'>, value: string]
    | [listField: 'glossary', id: string, itemField: keyof Omit<GlossaryTerm, 'id'>, value: string]
    | [listField: 'bibliography', id: string, itemField: keyof Omit<BibliographyEntry, 'id'>, value: string];

// For consolidated AI generation of book structure text elements - synced with server types
export type BookStructureTextElementKey =
    | 'dedication'
    | 'prologue'
    | 'preface'
    | 'introduction'
    | 'acknowledgmentsFront'
    | 'epigraph'
    | 'tableOfContents'
    | 'epilogue'
    | 'afterword'
    | 'authorsNote'
    | 'acknowledgmentsBack'
    | 'readingGroupGuide'
    | 'teaserNextBook'
    | 'aboutTheAuthor'
    | 'index'
    | 'isbn'
    | 'publisher'
    | 'edition'
    | 'copyrightNotice'
    | 'titleImagePrompt'
    | 'backCoverImagePrompt';

// Image generation elements for book structure
export type BookStructureImageElementKey =
    | 'titleImage'
    | 'backCoverImage';

// Combined element key for mixed text and image generation
export type BookStructureElementKey =
    | BookStructureTextElementKey
    | BookStructureImageElementKey;

// Table of Contents related interfaces
export interface TOCGenerationOptions {
  style: 'standard' | 'detailed' | 'digital' | 'minimalist' | 'academic';
  includeSubsections: boolean;
  showPageNumbers: boolean;
  showWordCounts: boolean;
  includeEmptySections: boolean;
}

export interface GeneratedBookStructureTexts {
    [key: string]: string; 
    // Example:
    // dedication?: string;
    // prologue?: string;
    // tableOfContents?: string;
    // index?: string;
    // ... and so on for all generatable elements
}

// Specific interfaces for AI responses for new elements
export interface GeneratedTableOfContents { tableOfContents: string; }
export interface GeneratedIndex { indexContent: string; }

export type ImageGenerationContextType = 'coverArt' | 'titlePage' | 'backCover';

export interface ContentItem {
    id: string;
    title: string;
    content: string;
    type: 'front-matter' | 'chapter' | 'back-matter';
}

export interface ImageGenerationPromptData {
    bookTitle: string;
    genre: string;
    themes: string[]; // Changed to array
    authorName?: string;
    logLine?: string;
    overallMood?: string;
    keySceneElement?: string;
}

export interface ExportRecord {
    id: string;
    timestamp: Date;
    format: 'pdf' | 'epub' | 'docx' | 'txt';
    filename: string;
    fileSize: number;
    qualityScore?: number;
    exportSettings: Record<string, any>;
    downloadCount: number;
}

export interface GenerateBlueprintResponse {
    projectId: number;
    blueprint: {
        chapters: Chapter[];
        bookMetadata?: BookMetadata;
        worldBuildingSuggestions?: WorldBuildingData;
    };
    creditsLeft: number;
    message: string;
}

export interface ProjectManagementControls {
    saveProject: () => Promise<void>;
    deleteProject: () => Promise<void>;
    isLoading: boolean;
    confirmDiscardProjectChanges: (message?: string) => boolean;
}

// --- Book Template System ---
export interface BookTemplate {
    id: string;
    name: string;
    genre: string;
    description: string;
    fictionVsNonFiction: 'Fiction' | 'Non-fiction';
    frontMatterDefaults: Partial<FrontMatter>;
    backMatterDefaults: Partial<BackMatter>;
    publishingDefaults: Partial<PublishingEssentials>;
    bookMetadataDefaults: Partial<BookMetadata>;
    authorDetailsDefaults?: Partial<AuthorDetails>;
    targetWordCount?: number;
    suggestedStructure?: string;
    commonElements: string[];
    professionalTips: string[];
}

export interface TemplateCategory {
    id: string;
    name: string;
    description: string;
    templates: BookTemplate[];
}
// --- End Book Template System ---

// --- Context-Aware Generation System ---
export interface GenerationContext {
    bookProgress: {
        hasChapters: boolean;
        chapterCount: number;
        estimatedWordCount: number;
        completionStatus: 'planning' | 'writing' | 'editing' | 'finalizing';
    };
    previouslyGenerated: string[];
    userPreferences: {
        formalityLevel: 'casual' | 'standard' | 'formal' | 'academic';
        lengthPreference: 'concise' | 'standard' | 'detailed';
        toneConsistency: 'relaxed' | 'professional' | 'creative' | 'scholarly';
    };
    contentContext: {
        bookMetadata: BookMetadata;
        worldBuildingSuggestions: WorldBuildingData;
        authorDetails: AuthorDetails;
        existingContent: {
            frontMatter: Partial<FrontMatter>;
            backMatter: Partial<BackMatter>;
            chapterSummaries: string[];
        };
    };
    generationHistory: GenerationRecord[];
}

export interface GenerationRecord {
    elementKey: BookStructureElementKey;
    timestamp: number;
    prompt: string;
    result: string;
    userRating?: 1 | 2 | 3 | 4 | 5;
    wasEdited: boolean;
}

export interface ContextualPromptEnhancement {
    basePrompt: string;
    contextualAdditions: string[];
    consistencyChecks: string[];
    styleGuidelines: string[];
    lengthGuidance: string;
}
// --- End Context-Aware Generation System ---
